import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ListAltIcon from "@material-ui/icons/ListAlt";
import Tooltip from "@material-ui/core/Tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ReminderInstancesDrawer from "../../components/ReminderInstancesDrawer";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const DEFAULT_REMINDERS_LIST = [
  {
    id: "1",
    name: "Confirmar Consulta",
    color: "#0284c7",
    template: "Olá {nome}, tudo bem? Passando para confirmar sua consulta agendada para amanhã. Podemos confirmar sua presença?"
  },
  {
    id: "2",
    name: "Reativar Conversa",
    color: "#d97706",
    template: "Olá {nome}, como vai? Passando para saber se você ficou com alguma dúvida ou gostaria de agendar seu atendimento!"
  },
  {
    id: "3",
    name: "Follow-up / Retorno",
    color: "#16a34a",
    template: "Olá {nome}! Tudo bem? Gostaria de saber como você está passando e agendar seu retorno!"
  }
];

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const RemindersPage = () => {
  const classes = useStyles();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);

  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewingReminder, setViewingReminder] = useState(null);

  const [name, setName] = useState("");
  const [color, setColor] = useState("#0284c7");
  const [template, setTemplate] = useState("");

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      const setting = data.find((s) => s.key === "reminders_list");
      if (setting && setting.value) {
        setReminders(JSON.parse(setting.value));
      } else {
        setReminders(DEFAULT_REMINDERS_LIST);
        await api.put("/settings/reminders_list", {
          value: JSON.stringify(DEFAULT_REMINDERS_LIST),
        });
      }
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  const handleOpenModal = (rem = null) => {
    if (rem) {
      setSelectedReminder(rem);
      setName(rem.name);
      setColor(rem.color || "#0284c7");
      setTemplate(rem.template || "");
    } else {
      setSelectedReminder(null);
      setName("");
      setColor("#0284c7");
      setTemplate("");
    }
    setModalOpen(true);
  };

  const handleSaveReminder = async () => {
    if (!name.trim()) {
      toast.error("Insira o nome do lembrete!");
      return;
    }

    let newList = [...reminders];
    if (selectedReminder) {
      newList = newList.map((item) =>
        item.id === selectedReminder.id ? { ...item, name, color, template } : item
      );
    } else {
      newList.push({
        id: Date.now().toString(),
        name,
        color,
        template,
      });
    }

    try {
      await api.put("/settings/reminders_list", {
        value: JSON.stringify(newList),
      });
      setReminders(newList);
      toast.success("Modelo de Lembrete salvo com sucesso!");
      setModalOpen(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteReminder = async (id) => {
    const newList = reminders.filter((item) => item.id !== id);
    try {
      await api.put("/settings/reminders_list", {
        value: JSON.stringify(newList),
      });
      setReminders(newList);
      toast.success("Lembrete removido!");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Modelos de Lembretes & Mensagens</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal()}
          >
            + Criar Novo Lembrete
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome do Lembrete</TableCell>
              <TableCell align="center">Cor</TableCell>
              <TableCell>Mensagem Padrão (Modelo)</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reminders.map((rem) => (
              <TableRow key={rem.id}>
                <TableCell style={{ fontWeight: "bold" }}>{rem.name}</TableCell>
                <TableCell align="center">
                  <span
                    style={{
                      display: "inline-block",
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      backgroundColor: rem.color || "#0284c7",
                      verticalAlign: "middle"
                    }}
                  />
                </TableCell>
                <TableCell style={{ color: "#475569" }}>
                  {rem.template || "Sem mensagem padrão definida"}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Ver Lembretes Cadastrados">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setViewingReminder(rem);
                        setViewDrawerOpen(true);
                      }}
                    >
                      <ListAltIcon />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => handleOpenModal(rem)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteReminder(rem.id)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedReminder ? "Editar Modelo de Lembrete" : "Novo Modelo de Lembrete"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nome do Lembrete"
            fullWidth
            variant="outlined"
            margin="dense"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Confirmar Consulta"
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <Typography variant="body2" style={{ fontWeight: "bold" }}>Cor da Etiqueta:</Typography>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{ width: 40, height: 35, border: "none", cursor: "pointer" }}
            />
          </div>

          <TextField
            label="Mensagem Padrão para Envio (Use {nome} para o nome do cliente)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder="Olá {nome}, passando para confirmar sua consulta..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveReminder} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ReminderInstancesDrawer
        open={viewDrawerOpen}
        onClose={() => setViewDrawerOpen(false)}
        reminderType={viewingReminder}
      />
    </MainContainer>
  );
};

export default RemindersPage;
